
import React, { useState } from 'react';
import { PlayerProfile } from '../types';
import { Heart, Shield, Activity, Search, FlaskConical } from 'lucide-react';
import { UPGRADE_PRICES } from '../constants';
import { ExitButton } from './ExitButton';

interface HealerClinicProps {
  player: PlayerProfile;
  updatePlayer: (updates: Partial<PlayerProfile>) => void;
  onExit: () => void;
}

export const HealerClinic: React.FC<HealerClinicProps> = ({ player, updatePlayer, onExit }) => {
  const [hanMessage, setHanMessage] = useState("Ah, ah, ah! In order to heal a part, we must understand the whole.");

  const restoreSpirit = () => {
    // Han's "Diagnosis" heals everyone a bit for a flat fee
    if (player.cash >= UPGRADE_PRICES.HAN_CONSULTATION) {
      // Heals 25 Spirit for everyone
      updatePlayer({
          cash: player.cash - UPGRADE_PRICES.HAN_CONSULTATION,
          group: {
            ...player.group,
            members: player.group.members.map(m => ({ ...m, spirit: Math.min(m.maxSpirit, m.spirit + 25) }))
          }
      });
      setHanMessage("Hmm... I see... lots of walls. But focusing on one part leads to ignoring others.");
    }
  };

  const buyTonic = () => {
      if (player.cash >= UPGRADE_PRICES.HAN_TONIC) {
          updatePlayer({
              cash: player.cash - UPGRADE_PRICES.HAN_TONIC,
              tonicCount: (player.tonicCount || 0) + 1
          });
          setHanMessage("A special tonic. (Rumi: 'This is just grape juice with a sticker on it.')");
      }
  };

  const buyTonicMax = () => {
    const cost = UPGRADE_PRICES.HAN_TONIC;
    const currentCount = player.tonicCount || 0;
    const MAX_ITEMS = 99;

    if (currentCount >= MAX_ITEMS) {
      setHanMessage("Your satchel is already full of tonics.");
      return;
    }
    if (player.cash < cost) {
      setHanMessage("You don't have enough for even one tonic.");
      return;
    }

    const affordableCount = Math.floor(player.cash / cost);
    const spaceAvailable = MAX_ITEMS - currentCount;
    const numToBuy = Math.min(affordableCount, spaceAvailable);

    if (numToBuy <= 0) {
      setHanMessage("Not enough cash to buy another tonic.");
      return;
    }

    const totalCost = numToBuy * cost;
    updatePlayer({
      cash: player.cash - totalCost,
      tonicCount: currentCount + numToBuy,
    });
    setHanMessage(`You bought ${numToBuy} tonics. Use them well.`);
  };

  const buyWard = () => {
      if (player.cash >= UPGRADE_PRICES.MYSTIC_WARD) {
          updatePlayer({
              cash: player.cash - UPGRADE_PRICES.MYSTIC_WARD,
              mysticWards: player.mysticWards + 1
          });
          setHanMessage("The ancient protection is strong. It will shield you from failure.");
      }
  };

  const buyWardMax = () => {
    const cost = UPGRADE_PRICES.MYSTIC_WARD;
    const currentCount = player.mysticWards;
    const MAX_ITEMS = 99;

    if (currentCount >= MAX_ITEMS) {
      setHanMessage("You are protected by enough wards for a lifetime.");
      return;
    }
    if (player.cash < cost) {
      setHanMessage("An ancient ward is... very expensive.");
      return;
    }

    const affordableCount = Math.floor(player.cash / cost);
    const spaceAvailable = MAX_ITEMS - currentCount;
    const numToBuy = Math.min(affordableCount, spaceAvailable);

    if (numToBuy <= 0) {
        setHanMessage("Not enough cash to acquire another ward.");
        return;
    }

    const totalCost = numToBuy * cost;
    updatePlayer({
      cash: player.cash - totalCost,
      mysticWards: currentCount + numToBuy,
    });
    setHanMessage(`You've acquired ${numToBuy} wards. Your spirit is well-guarded.`);
  };

  return (
    <div className="h-full w-full bg-stone-950 flex flex-col items-center justify-center p-8 relative">
       {/* Background: Suggesting rows of wooden drawers */}
       <div className="absolute inset-0 opacity-20" style={{ 
           backgroundImage: 'linear-gradient(to right, #44403c 1px, transparent 1px), linear-gradient(to bottom, #44403c 1px, transparent 1px)',
           backgroundSize: '40px 20px',
           backgroundColor: '#1c1917'
       }}></div>
       
       <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-900/30 via-stone-950/80 to-stone-950"></div>

       <ExitButton onExit={onExit} />

       <div className="relative z-10 w-full max-w-4xl glass-panel p-10 rounded-xl border border-stone-700 shadow-2xl flex flex-col items-center">
            
            <div className="text-center mb-8 w-full border-b border-stone-700 pb-6">
                <div className="flex items-center justify-center gap-3 mb-2">
                    <div className="p-2 bg-stone-800 rounded-full border border-stone-600">
                        <Activity className="text-green-500" size={32} />
                    </div>
                    <h2 className="text-4xl font-heading text-stone-200">HEALER HAN'S CLINIC</h2>
                </div>
                <p className="text-stone-400 italic text-lg">"{hanMessage}"</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                
                {/* Consultation */}
                <div className="bg-stone-900/80 p-6 rounded-lg border border-stone-700 hover:border-green-500 transition-colors flex flex-col items-center text-center group">
                    <Search size={48} className="text-green-500 mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-xl font-bold text-white mb-2">Full Diagnosis</h3>
                    <p className="text-stone-400 text-sm mb-6 min-h-[40px]">Han reads your aura and provides holistic healing for the group.</p>
                    <button 
                        onClick={restoreSpirit}
                        disabled={player.cash < UPGRADE_PRICES.HAN_CONSULTATION || player.group.members.every(m => m.spirit === m.maxSpirit)}
                        className="w-full py-3 bg-stone-700 hover:bg-green-700 disabled:bg-stone-800 disabled:text-stone-600 text-white font-bold rounded-lg transition-colors border border-stone-600"
                    >
                        HEAL ALL (+25 SP) ${UPGRADE_PRICES.HAN_CONSULTATION}
                    </button>
                </div>

                {/* Secret Tonic */}
                <div className="bg-stone-900/80 p-6 rounded-lg border border-stone-700 hover:border-purple-500 transition-colors flex flex-col items-center text-center group">
                    <div className="relative">
                        <FlaskConical size={48} className="text-purple-400 mb-4 group-hover:rotate-12 transition-transform" />
                        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">HOT</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Han's Secret Tonic</h3>
                    <p className="text-stone-400 text-sm mb-6 min-h-[40px]">Totally legit "tonic" (Store-bought grape juice). Restores Honmoon.</p>
                    <div className="w-full mt-auto">
                        <div className="text-xs text-stone-500 uppercase font-bold mb-2">In Stock: {player.tonicCount || 0} / 99</div>
                        <div className="flex rounded-lg overflow-hidden border border-purple-800">
                            <button 
                                onClick={buyTonic}
                                disabled={player.cash < UPGRADE_PRICES.HAN_TONIC || (player.tonicCount || 0) >= 99}
                                className="flex-1 py-3 bg-purple-900/50 hover:bg-purple-800/80 disabled:bg-stone-800 disabled:text-stone-600 text-purple-200 font-bold transition-colors"
                            >
                                BUY (+1) ${UPGRADE_PRICES.HAN_TONIC}
                            </button>
                            <button 
                                onClick={buyTonicMax}
                                disabled={player.cash < UPGRADE_PRICES.HAN_TONIC || (player.tonicCount || 0) >= 99}
                                className="font-black px-4 py-3 bg-purple-800 hover:bg-purple-700 disabled:bg-stone-800 disabled:text-stone-600 text-white transition-colors"
                            >
                                MAX
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Ancient Ward */}
                <div className="bg-stone-900/80 p-6 rounded-lg border border-stone-700 hover:border-amber-500 transition-colors flex flex-col items-center text-center group">
                    <Shield size={48} className="text-amber-500 mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-xl font-bold text-white mb-2">Acquire Ward</h3>
                    <p className="text-stone-400 text-sm mb-6 min-h-[40px]">Ancient insurance against a career-ending disaster.</p>
                    <div className="w-full mt-auto">
                        <div className="text-xs text-stone-500 uppercase font-bold mb-2">Active Wards: {player.mysticWards} / 99</div>
                         <div className="flex rounded-lg overflow-hidden border border-stone-600">
                            <button 
                                onClick={buyWard}
                                disabled={player.cash < UPGRADE_PRICES.MYSTIC_WARD || player.mysticWards >= 99}
                                className="flex-1 py-3 bg-stone-700 hover:bg-amber-700 disabled:bg-stone-800 disabled:text-stone-600 text-white font-bold transition-colors"
                            >
                                BUY (+1) ${UPGRADE_PRICES.MYSTIC_WARD.toLocaleString()}
                            </button>
                            <button 
                                onClick={buyWardMax}
                                disabled={player.cash < UPGRADE_PRICES.MYSTIC_WARD || player.mysticWards >= 99}
                                className="font-black px-4 py-3 bg-amber-800 hover:bg-amber-700 disabled:bg-stone-800 disabled:text-stone-600 text-white transition-colors"
                            >
                                MAX
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 flex gap-4 text-xs text-stone-500">
                <div className="flex items-center gap-2">
                    <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Han&clothing=collarAndSweater&eyebrows=defaultNatural" alt="Han" className="w-8 h-8 rounded-full grayscale opacity-50" />
                    <span>"Rumi, stop trying to peel the stickers off my tonics."</span>
                </div>
            </div>
       </div>
    </div>
  );
};
